import {
  createContext,
  ReactNode,
  useRef,
  useMemo,
  useContext,
  useCallback,
  useState,
  useEffect,
} from 'react';
import debounce from 'lodash/debounce';
import { ApolloError } from '@apollo/client';

import {
  ACTIVITIES_ANSWER_DRAW_TYPE,
  ACTIVITIES_ANSWER_TEXT,
  ActivitiesComponentType,
} from 'global/constants';
import { useGetInteractiveLessonParams } from 'approot/activity-builder/activity-builder.hooks';
import { useSnackbar } from 'approot/shared/snackbar-notification/snackbar-notification.hooks';
import { useStudentMutation } from 'lib/apollo/student-apollo-hooks';
import { isPermanentApolloError } from 'lib/apollo.utils';
import type { AnswerFragment } from '../../present/activity-answers.graphql.generated';
import type {
  UpsertStudentActivityAnswerMutation,
  UpsertStudentActivityAnswerMutationVariables,
} from '../../student/answer/student-answer.graphql.generated';
import { GET_MY_ANSWERS } from 'approot/activity-builder/present/activity-answers.graphql';
import { UPSERT_STUDENT_ACTIVITY_ANSWER } from 'approot/activity-builder/student/answer/student-answer.graphql';
import type {
  GetMyAnswersQuery,
  GetMyAnswersQueryVariables,
} from '../../present/activity-answers.graphql.generated';
import { removeTypename } from 'approot/shared/logic/remove-typename';
import { upsertAnswerInCache } from 'approot/activity-builder/present/activity-answer.apollo';
import { pushStudentAnswerEvent } from 'approot/activity-builder/components/shared/student-answer-data-tracking.utils';
import type { DrawAnswerFragment } from '../../present/activity-answers.graphql.generated';
import { useActivityBuilderRequest } from 'approot/activity-builder/activity-builder-request.apollo';
import { useStudentStartAssignmentMutation } from 'approot/assignments/hooks/use-student-start-assignment-mutation';

export type AnswerInput = AnswerFragment;

export type AnswerUpdateCallback = (answer: AnswerInput) => void;

export type UpdateProps = {
  input: AnswerInput;
  variables: GetMyAnswersQueryVariables;
  dataTrackingComponentType?: ActivitiesComponentType;
};

type BatchUpdate = {
  loading: boolean;
  updateAnswerApollo: (props: UpdateProps) => void;
  updateAnswer: (props: UpdateProps) => void;
  upsertAnswerData: UpsertStudentActivityAnswerMutation | null | undefined;
};

export const BatchStudentAnswersContext = createContext<BatchUpdate>({
  loading: false,
  updateAnswer: () => {},
  updateAnswerApollo: () => {},
  upsertAnswerData: null,
});

export const BatchStudentAnswersContextProvider = ({
  children,
  onUpsertError,
  onUpsertSuccess,
  skipAssignmentStartedCall,
}: {
  children: ReactNode;
  onUpsertError?: (error: ApolloError) => void;
  onUpsertSuccess?: () => void;
  /**
   * When false (digital assessment, student still not-started), the
   * `studentStartAssignment` mutation runs once on the first answer change.
   * When true (already in progress / submitted, or a lesson), that mutation is
   * not triggered from this provider.
   */
  skipAssignmentStartedCall?: boolean;
}) => {
  const { showError } = useSnackbar();

  const answerSyncMapRef = useRef<Map<string, Function>>(new Map());
  const hasCalledStartRef = useRef<boolean>(false);
  const [upsertAnswerData, setUpsertAnswerData] = useState<
    UpsertStudentActivityAnswerMutation | null | undefined
  >(null);

  const { contentId, contentType, version, assignmentId, classId } =
    useGetInteractiveLessonParams();
  const { createRequest, clearRequest } = useActivityBuilderRequest();

  const { startAssignment } = useStudentStartAssignmentMutation({
    assignmentId,
    classId: classId!,
  });

  // Reset the guard if the status changes back to not-started (e.g. teacher resets)
  useEffect(() => {
    if (skipAssignmentStartedCall === false) {
      hasCalledStartRef.current = false;
    }
  }, [skipAssignmentStartedCall]);

  const [upsertAnswer, { loading, client }] = useStudentMutation<
    UpsertStudentActivityAnswerMutation,
    UpsertStudentActivityAnswerMutationVariables
  >(UPSERT_STUDENT_ACTIVITY_ANSWER, {
    onCompleted: result => {
      setUpsertAnswerData(result);
      onUpsertSuccess?.();
    },
    onError: error => {
      if (onUpsertError) {
        onUpsertError(error);
      } else {
        showError(
          'There was an error saving your answer. Please try again later.'
        );
      }
    },
  });

  const syncToServer = useCallback(
    async ({
      input,
      variables,
      requestId,
      onComplete,
    }: {
      input: AnswerInput;
      variables: GetMyAnswersQueryVariables;
      requestId: string;
      onComplete: (requestId: string) => void;
    }) => {
      if (!version) return;

      const answerProps =
        input.answerType === ACTIVITIES_ANSWER_DRAW_TYPE
          ? {
              __typename: 'DrawAnswer',
              answerType: ACTIVITIES_ANSWER_DRAW_TYPE,
              drawing: (input.answer as DrawAnswerFragment).drawing,
            }
          : input.answer;

      await upsertAnswer({
        fetchPolicy: 'no-cache',
        ignoreResults: true,
        variables: {
          input: {
            classId: variables.input.classId,
            contentType,
            contentId,
            componentId: input.componentId,
            contentVersion: version,
            uuid: input.uuid,
            activityId: input.activityId,
            answerReferences: input.answerReferences
              ? removeTypename(input.answerReferences)
              : null,
            answer: {
              ...removeTypename(answerProps),
              answerType: input.answerType || ACTIVITIES_ANSWER_TEXT,
            },
            assignmentId,
          },
        },
      })
        .catch(() => {
          showError(
            'We had trouble saving your data. Please refresh and check your answers.'
          );
        })
        .finally(() => {
          onComplete(requestId);
        });
    },
    [contentId, contentType, version, upsertAnswer, showError, assignmentId]
  );

  const updateAnswerApollo = useCallback(
    ({
      input,
      variables,
      dataTrackingComponentType,
    }: {
      input: AnswerInput;
      variables: GetMyAnswersQueryVariables;
      dataTrackingComponentType?: ActivitiesComponentType;
    }) => {
      const answersData = client.readQuery<GetMyAnswersQuery>({
        query: GET_MY_ANSWERS,
        variables,
      });

      const isNewAnswer = !answersData?.getMyAnswers.some(
        a =>
          a.componentId === input.componentId &&
          a.activityId === input.activityId
      );

      upsertAnswerInCache({
        answer: input,
        answers: answersData?.getMyAnswers || [],
        client,
        variables,
      });

      if (!answerSyncMapRef.current.get(input.uuid)) {
        // create a new debounce function for the answer
        answerSyncMapRef.current.set(
          input.uuid,
          debounce(input => {
            const requestId = createRequest(input.componentId);
            syncToServer({
              input,
              variables,
              requestId,
              onComplete: (requestId: string) => {
                clearRequest(requestId);
              },
            });
          }, 300)
        );
      }

      // call the debounced function for this answer
      const sync = answerSyncMapRef.current.get(input.uuid);
      if (sync) {
        sync(input);
        if (isNewAnswer && version) {
          pushStudentAnswerEvent({
            answer: input,
            contentId,
            contentType,
            version,
            dataTrackingComponentType,
          });
        }
      }

      // Fire studentStartAssignment once on the first answer while the student is
      // still not-started (`skipAssignmentStartedCall` is false in that case).
      // Deliberately not gated on isAutomarkable: progress is tracked for every
      // digital assessment, so a later automarkable flip finds the row already
      // written (TT-1649).
      if (
        !skipAssignmentStartedCall &&
        assignmentId &&
        classId &&
        !hasCalledStartRef.current
      ) {
        hasCalledStartRef.current = true;
        startAssignment({
          variables: {
            input: {
              assignmentId,
              classId,
            },
          },
        }).catch((error: ApolloError) => {
          // Unlatch so the next saved answer retries. The row is what the
          // teacher's progress view reads, so dropping it on one failed request
          // would show "not started" over real answers.
          //
          // A refusal the server will repeat verbatim stays latched: this runs
          // on every answer change, and `studentProgress` still reads
          // `not-started` while the mutation is being rejected, so retrying a
          // permanent failure (a closed assignment, no class access) would
          // re-fire on every keystroke for the rest of the mount.
          if (!isPermanentApolloError(error)) {
            hasCalledStartRef.current = false;
          }
        });
      }
    },
    [
      client,
      contentId,
      contentType,
      version,
      syncToServer,
      createRequest,
      clearRequest,
      skipAssignmentStartedCall,
      assignmentId,
      classId,
      startAssignment,
    ]
  );

  const updateAnswer = useCallback(
    ({ input, variables, dataTrackingComponentType }: UpdateProps) => {
      if (!input) return;

      updateAnswerApollo({
        input,
        variables,
        dataTrackingComponentType,
      });
    },
    [updateAnswerApollo]
  );

  const value = useMemo(
    () => ({
      loading,
      updateAnswer,
      updateAnswerApollo,
      upsertAnswerData,
    }),
    [loading, updateAnswer, updateAnswerApollo, upsertAnswerData]
  );

  return (
    <BatchStudentAnswersContext.Provider value={value}>
      {children}
    </BatchStudentAnswersContext.Provider>
  );
};

export const useBatchStudentAnswersContext = () => {
  const { loading, updateAnswer, updateAnswerApollo, upsertAnswerData } =
    useContext(BatchStudentAnswersContext);

  const handleUpdate = useCallback(
    (props: UpdateProps) => {
      updateAnswer(props);
    },
    [updateAnswer]
  );

  return {
    loading,
    handleUpdate,
    updateAnswer,
    updateAnswerApollo,
    upsertAnswerData: upsertAnswerData?.upsertStudentActivityAnswer,
  };
};
